import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { loginUser } from "../services/auth";
import { clearUserStorage, getDeviceId, getRememberMe, saveRememberMe, saveUserToken } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";
import ForgotPasswordPopup from "./ForgotPasswordPopup";
import GoogleLoginButton from "../components/GoogleLoginButton";

function LoginPopup({ onLoginSuccess }: { onLoginSuccess?: () => void }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const forgotTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const saved = getRememberMe();
    if (saved) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await loginUser(email, password, getDeviceId());
      clearUserStorage();
      saveUserToken({
        token: res.data.token,
        expiration: res.data.expiration,
        secretKey: res.data.secretKey,
        userId: res.data.userId,
        refreshToken: res.data.refreshToken,
        refreshTokenExpiration: res.data.refreshTokenExpiration,
      });
      if (rememberMe) {
        saveRememberMe(email, password);
      }
      onLoginSuccess?.();
      closeButtonRef.current?.click();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        const description = Array.isArray(data) ? data[0]?.description : null;
        setError(description ?? t("login.error"));
      } else {
        setError(t("login.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    const loginEl = document.getElementById("loginModal");
    if (!loginEl) return;
    const handleHidden = () => {
      loginEl.removeEventListener("hidden.bs.modal", handleHidden);
      forgotTriggerRef.current?.click();
    };
    loginEl.addEventListener("hidden.bs.modal", handleHidden);
    closeButtonRef.current?.click();
  };

  return (
    <>
    <button ref={forgotTriggerRef} className="d-none" data-bs-toggle="modal" data-bs-target="#forgotPasswordModal" />
    <div
      className="modal fade"
      id="loginModal"
      tabIndex={-1}
      aria-labelledby="loginModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header border-0 pb-0">
            <button
              ref={closeButtonRef}
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            ></button>
          </div>

          <div className="modal-body px-4 pb-4">
            <div className="text-center mb-4">
              <i className="bi bi-person-circle" style={{ fontSize: "3.5rem", color: "#c20e1a" }}></i>
              <h5 className="mt-2 fw-bold" id="loginModalLabel">{t("login.title")}</h5>
              <p className="text-muted small">{t("login.subtitle")}</p>
            </div>

            <div className="mb-3">
              <label htmlFor="loginEmail" className="form-label fw-semibold">
                {t("login.email")}
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-envelope"></i>
                </span>
                <input
                  type="email"
                  className="form-control"
                  id="loginEmail"
                  placeholder={t("login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="loginPassword" className="form-label fw-semibold">
                {t("login.password")}
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-lock"></i>
                </span>
                <input
                  type="password"
                  className="form-control"
                  id="loginPassword"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="form-check mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label className="form-check-label text-muted small" htmlFor="rememberMe">
                {t("login.rememberMe")}
              </label>
            </div>

            {error && (
              <div className="alert alert-danger py-2 small mb-3">{error}</div>
            )}

            <button
              type="button"
              className="btn btn-danger w-100 rounded-pill py-2 fw-semibold"
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {t("login.loading")}
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {t("login.title")}
                </>
              )}
            </button>

            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link btn-sm text-muted text-decoration-none p-0"
                onClick={handleForgotPassword}
              >
                <i className="bi bi-question-circle me-1"></i>
                {t("forgotPassword.link")}
              </button>
            </div>

            <div className="d-flex align-items-center my-3">
              <hr className="flex-grow-1" />
              <span className="px-2 text-muted small">veya</span>
              <hr className="flex-grow-1" />
            </div>

            <GoogleLoginButton onLoginSuccess={() => { onLoginSuccess?.(); closeButtonRef.current?.click(); }} />
          </div>
        </div>
      </div>
    </div>

    <ForgotPasswordPopup />
    </>
  );
}

export default LoginPopup;
